import React from 'react';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Avatar, usageText, mdComponents } from './ArtifactHistory';

/** A collection artifact: an intro (Markdown, may carry a diagram) and cards linking to the artifacts it collects. */
const Intro = styled.div`
  font-size: 16.5px; line-height: 1.55; margin: 8px 0 28px;
  h2 { font-size: 22px; margin: 32px 0 8px; } h3 { font-size: 17px; margin: 20px 0 6px; }
  ul, ol { padding-left: 22px; } li { margin: 4px 0; } p { margin: 0 0 12px; }
  pre.mermaid, .mermaid { margin: 12px 0; }
`;
const Grid = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 14px; `;
const CardLink = styled.a`
  display: block; text-decoration: none; color: inherit; background: #fff; border: 1px solid var(--color-grey-4, #d9d9d9); border-radius: 10px; padding: 16px 18px;
  &:hover { border-color: var(--color-primary); box-shadow: 0 2px 10px rgba(0,0,0,.06); }
  .eyebrow { font-size: 11.5px; text-transform: uppercase; letter-spacing: .08em; color: var(--color-primary); margin: 0 0 6px; }
  .q { font-size: 17px; font-weight: 700; line-height: 1.25; margin: 0 0 10px; }
  .meta { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--color-grey-2, #636363); flex-wrap: wrap; }
  .nums { font-size: 12.5px; color: var(--color-grey-2, #636363); margin-top: 8px; font-variant-numeric: tabular-nums; }
`;
const Avatars = styled.span` display: inline-flex; & > * + * { margin-left: -6px; } `;
const H2 = styled.h2` font-size: 22px; margin: 36px 0 12px; padding-top: 12px; border-top: 1px solid var(--color-grey-4, #d9d9d9); font-weight: 700; `;

function numsText(item, t) {
  const n = item.nums;
  if (!n) return t('chatAgent.thread.turns', { n: item.turns });
  if ('findings' in n) return [t('chatAgent.thread.findings', { n: n.findings }), t('chatAgent.thread.tensions', { n: n.tensions }), t('chatAgent.thread.gaps', { n: n.gaps })].join(' · ');
  return [t('chatAgent.thread.agreed', { n: n.agreed }), t('chatAgent.thread.disputed', { n: n.disputed }), t('chatAgent.thread.open', { n: n.open })].join(' · ');
}

export default function CollectionView({ snapshot }) {
  const { t, language } = useTranslation();
  const items = snapshot.items || [];
  return (
    <>
      {snapshot.intro && <Intro><ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{snapshot.intro}</ReactMarkdown></Intro>}
      <H2>{t('artifact.collectionItems', { n: items.length })}</H2>
      <Grid>
        {items.map((it) => (
          <CardLink key={it.hash} href={`/artifact/${it.hash}`}>
            <p className="eyebrow">{t(`chatAgent.thread.kind.${it.threadKind || 'bounce'}`)}{it.rounds ? ` · ${t('chatAgent.thread.rounds', { n: it.rounds })}` : ''}</p>
            <p className="q">{it.question}</p>
            <div className="meta">
              <Avatars>{(it.participants || []).map((p) => <Avatar key={p.slug} slug={p.slug} avatarUrl={p.avatarUrl} size={22} />)}</Avatars>
              <span>{(it.participants || []).map((p) => String(p.slug).toUpperCase()).join(' · ')}</span>
            </div>
            <p className="nums">{numsText(it, t)}{it.usage ? <><br />{usageText(it.usage, t, language)}</> : null}</p>
          </CardLink>
        ))}
      </Grid>
    </>
  );
}
